import React from 'react';
import { Button, Grid, TextField } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useAuth } from "hooks";

import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useLazyGetDDLShipmentListQuery } from "store/services/shipmentApi";

const INITIAL_STATE = {
  account: "",
  entity: "",
  sourceBranch: "",
  destinationBranch: "",
  shipmentId: [],
  legDistance: "",
  legTime: "",
  legCost: "",
  isActive: true
};

const LegForm = ({ isBusy = null, legDetail = null, onSubmit = () => { } }) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });

  const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] = useLazyGetDDLEntityListQuery();
  const [fetchBranches, { data: branches, isLoading: loadingBranches }] = useLazyGetDDLBranchListQuery();
  const [fetchShipments, { data: shipments, isLoading: loadingShipments }] =
    useLazyGetDDLShipmentListQuery();
  const handleOnLegSubmit = (data) => {
    const payload = {
      ...data,
      entity: data?.entity?._id,
      account: data?.account?._id,
      sourceBranch: data?.sourceBranch?._id,
      destinationBranch: data?.destinationBranch?._id
    };
    onSubmit(payload);
  };

  React.useEffect(() => {
    if (!legDetail) return;
    reset(legDetail);
  }, [legDetail, reset]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin)
      fetchEntities({ account: _watchAccount?._id ?? viewEntity?.entity?.account?._id });
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin, viewEntity?.entity?.account]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchBranches({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchBranches]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchShipments({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchShipments]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          {/* Shipment ID */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select a Shipment ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  multiple
                  label="Shipment ID"
                  required
                  value={value || []}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) => option?.shipmentId ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={shipments || []}
                  loading={loadingShipments}
                  loadingText="Loading Shipments..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="sourceBranch"
              control={control}
              rules={{ required: "Please select source branch." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Source Branch"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                  }}
                  getOptionLabel={(option) => option && `${option?.name} - ${option?.code}`}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={branches || []}
                  loading={loadingBranches}
                  loadingText="Loading Source Branch..."
                  helperText={errors?.sourceBranch?.message}
                  error={!!errors?.sourceBranch}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="destinationBranch"
              control={control}
              rules={{ required: "Please select destination branch." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Destination Branch"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                  }}
                  getOptionLabel={(option) => option && `${option?.name} - ${option?.code}`}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={branches || []}
                  loading={loadingBranches}
                  loadingText="Loading Destination Branch..."
                  helperText={errors?.destinationBranch?.message}
                  error={!!errors?.destinationBranch}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="legDistance"
              control={control}
              rules={{ required: "Distance is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Leg Distance (km)"
                  type="number"
                  fullWidth
                  required
                  error={!!errors?.legDistance}
                  helperText={errors?.legDistance?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="legTime"
              control={control}
              rules={{ required: "Time is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Leg Time (minutes)"
                  type="number"
                  fullWidth
                  required
                  error={!!errors?.legTime}
                  helperText={errors?.legTime?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="legCost"
              control={control}
              rules={{ required: "Cost is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Leg Cost ($)"
                  type="number"
                  fullWidth
                  required
                  error={!!errors?.legCost}
                  helperText={errors?.legCost?.message}
                />
              )}
            />
          </Grid>

          {legDetail && (
             <Grid item xs={6}>
             <Controller
               name="isActive"
               control={control}
               render={({ field }) => (
                 <AppSwitch {...field} label="Is Active?" />
               )}
             />
           </Grid>
          )}
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnLegSubmit)}
        >
          {legDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default LegForm;
